import React from "react";
import { CircularProgress } from "@remo-co/ui-core/src/components/CircularProgress";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Form } from "@remo-co/ui-core/src/components/Form";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { InfoRounded } from "@remo-co/ui-core/src/icons/InfoRounded";
import classNames from "classnames";
import { useI18n } from "i18n";
import { isSecureURL, validateURL } from "helpers/urlHelper";
import { Field } from "react-final-form";
import { composeValidators } from "helpers/formHelper";
import { isTrialCompleted } from "modules/companyPlanSettings/utils/trial";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import InfoEditValidationSchema from "./InfoEditValidationSchema";
import SAMLSSOButton from "../authButtons/SAMLSSOButton";
import SsoHelperDialog from "./SsoHelperDialog";
import { useStyles } from "./styles";
import { BEGIN_CERTIFICATE_TEXT, END_CERTIFICATE_TEXT } from "./constants";
import { ISsoInfo } from "./types";

export interface IInfoEditProps {
  configInfo: ISsoInfo;
  onToggleInfoEditing: () => void;
  isNew?: boolean;
  onSyncProvider: (data: Partial<ISsoInfo>) => Promise<boolean>;
}

const InfoEdit = ({
  configInfo,
  isNew,
  onToggleInfoEditing,
  onSyncProvider,
}: IInfoEditProps) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const { t } = useI18n(["common", "sso"]);
  const { myCompanyPlan } = useCompanyPlanSettings();

  const handlePopoverClose = () => setAnchorEl(null);
  const handlePopoverOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);

  const onSubmit = async (values: Partial<ISsoInfo>) => {
    setIsLoading(true);
    const success = await onSyncProvider(values);

    setIsLoading(false);

    if (success) onToggleInfoEditing();
  };

  const urlValidator = (value: string): string | undefined => {
    if (!validateURL(value) || !isSecureURL(value)) {
      return t("sso:validation.endpoint");
    }

    return undefined;
  };

  const certificateValidator = (value: string): string | undefined => {
    if (
      value?.indexOf(BEGIN_CERTIFICATE_TEXT) !== 0 ||
      // eslint-disable-next-line no-unsafe-optional-chaining
      value?.indexOf(END_CERTIFICATE_TEXT) !== value?.length - 25
    ) {
      return t("sso:validation.certificate");
    }

    return undefined;
  };

  return (
    <>
      <div className={classes.split}>
        <Typography data-testid="title-element" variant="h4">
          {t("sso:info.edit.title")}
        </Typography>
        <Typography
          data-testid="info-icon"
          variant="h5"
          color="primary"
          onMouseEnter={handlePopoverOpen}
        >
          {t("sso:requirements")}
        </Typography>
        <SsoHelperDialog
          handlePopoverClose={handlePopoverClose}
          anchorEl={anchorEl}
        />
      </div>
      <Typography variant="body1" className={classes.description}>
        {t("sso:contact.support")}
      </Typography>

      <div className={classes.infoBox}>
        <div className={classes.darkIcon}>
          <InfoRounded />
        </div>
        <Typography className={classes.infoBoxText} variant="body1">
          {t("sso:sso.supported")}
        </Typography>
      </div>

      <Form
        initialValues={{
          displayName: configInfo.displayName,
          authenticationEndpoint: configInfo.authenticationEndpoint,
          identityProviderEntityID: configInfo.identityProviderEntityID,
          publicCertificate: configInfo.publicCertificate,
        }}
        onSubmit={onSubmit}
        validationSchema={InfoEditValidationSchema(t)}
      >
        <Field name="displayName">
          {({ input, meta }) => (
            <div className={classes.inputContainer}>
              <Input
                {...input}
                fullWidth
                type="text"
                label={t("sso:display.name")}
                id="displayName"
                disabled={isLoading}
                size="sm"
                error={meta.error && meta.touched ? meta.error : false}
                description={
                  !meta.error && meta.touched
                    ? t("character.remaining", {
                        key: `${20 - (input.value?.length ?? 0)}`,
                      })
                    : undefined
                }
              />

              {/* Sample preview button */}
              <div className={classes.samlSampleButton}>
                <Typography variant="h5">{t("sso:preview.label")}</Typography>
                <SAMLSSOButton
                  isPreview
                  providerName={configInfo.providerId || ""}
                >
                  {input.value
                    ? t("sso:login.with.provider", {
                        key: input.value,
                      })
                    : t("sso:login.with.placeholder")}
                </SAMLSSOButton>
              </div>
            </div>
          )}
        </Field>

        <Field
          name="authenticationEndpoint"
          validate={composeValidators(urlValidator)}
        >
          {({ input, meta }) => (
            <div className={classes.inputContainer}>
              <Input
                {...input}
                type="text"
                fullWidth
                label={t("sso:authentication.endpoint")}
                id="authenticationEndpoint"
                error={meta.error && meta.touched ? meta.error : false}
                disabled={isLoading}
                size="sm"
              />
            </div>
          )}
        </Field>

        <Field name="identityProviderEntityID">
          {({ input, meta }) => (
            <div className={classes.inputContainer}>
              <Input
                {...input}
                type="text"
                fullWidth
                label={t("sso:identity.provider.entity.id")}
                id="identityProviderEntityID"
                disabled={isLoading}
                error={meta.error && meta.touched ? meta.error : false}
                size="sm"
              />
            </div>
          )}
        </Field>

        <Field
          name="publicCertificate"
          validate={composeValidators(certificateValidator)}
        >
          {({ input, meta }) => (
            <div className={classes.inputContainer}>
              <Input
                {...input}
                type="text"
                label={t("sso:public.certificate")}
                size="multi"
                multiline
                rows={5}
                fullWidth
                id="publicCertificate"
                disabled={isLoading}
                error={meta.error && meta.touched ? meta.error : false}
                description={t("sso:certificate.helper")}
              />
            </div>
          )}
        </Field>

        <div className={classes.actionContainer}>
          <Button
            className={classes.actionButton}
            type="submit"
            data-testid="submit-button"
            disabled={isTrialCompleted(myCompanyPlan)}
            variant="secondary"
            color="blue"
            size="md"
          >
            {t("button.save")}
            {isLoading && (
              <CircularProgress
                color="secondary"
                size={20}
                className={classNames("loader", classes.spinner)}
              />
            )}
          </Button>
          {!isNew && (
            <Button
              variant="tertiary"
              color="dark"
              onClick={onToggleInfoEditing}
              size="md"
            >
              {t("button.cancel")}
            </Button>
          )}
        </div>
      </Form>
    </>
  );
};

export default InfoEdit;
